import {
  enumeratedLiterals,
  type EnumeratedLiteralType,
} from "./enumeratedLiterals";

export const CompareDateRangeTypes = enumeratedLiterals(
  [
    "previousPeriod",
    "lastPeriod",
    "samePeriodDifferentYear",
    "samePeriodPriorYear",
    "custom",
  ] as const,
  {
    getLabels: (accessors) =>
      ({
        [accessors.PREVIOUSPERIOD]: "Previous period",
        [accessors.LASTPERIOD]: "Last period",
        [accessors.SAMEPERIODDIFFERENTYEAR]: "Same period, different year",
        [accessors.SAMEPERIODPRIORYEAR]: "Same period, prior year",
        [accessors.CUSTOM]: "Custom",
      } as const),
  },
);

export type CompareDateRangeType = EnumeratedLiteralType<
  typeof CompareDateRangeTypes
>;

export type CompareDateRange<
  T extends CompareDateRangeType = CompareDateRangeType,
> =
  | {
      [CompareDateRangeTypes.CUSTOM]: {
        value: "custom";
        startDate: string;
        endDate: string;
      };
      [CompareDateRangeTypes.LASTPERIOD]: {
        value: "lastPeriod";
      };
      [CompareDateRangeTypes.PREVIOUSPERIOD]: {
        value: "previousPeriod";
      };
      [CompareDateRangeTypes.SAMEPERIODDIFFERENTYEAR]: {
        value: "samePeriodDifferentYear";
        year: number;
        calendar: "standard";
      };
      [CompareDateRangeTypes.SAMEPERIODPRIORYEAR]: {
        value: "samePeriodPriorYear";
      };
    }[T];

type CompareDateRangeTypeguardRT<
  T extends CompareDateRangeType | CompareDateRangeType[],
> = T extends CompareDateRangeType[]
  ? CompareDateRange<T[number]>
  : T extends CompareDateRangeType
  ? CompareDateRange<T>
  : never;

export const isCompareDateRangeOfType = <
  T extends CompareDateRangeType | CompareDateRangeType[],
>(
  range: CompareDateRange,
  type: T,
): range is CompareDateRangeTypeguardRT<T> => {
  if (Array.isArray(type)) {
    return type.includes(
      (range as CompareDateRange<(T & CompareDateRangeType[])[number]>).value,
    );
  }
  return (range as CompareDateRange<CompareDateRangeType>).value === type;
};

export const CompareDateRangeTypesV2 = enumeratedLiterals(
  ["previousPeriod", "samePeriodPriorYear", "custom"] as const,
  {
    getLabels: (accessors) =>
      ({
        [accessors.PREVIOUSPERIOD]: "Previous period",
        [accessors.SAMEPERIODPRIORYEAR]: "Same period, prior year",
        [accessors.CUSTOM]: "Custom",
      } as const),
  },
);

export type CompareDateRangeTypeV2 = EnumeratedLiteralType<
  typeof CompareDateRangeTypesV2
>;

export type CompareDateRangeV2<
  T extends CompareDateRangeTypeV2 = CompareDateRangeTypeV2,
> =
  | {
      [CompareDateRangeTypes.CUSTOM]: {
        value: "custom";
        startDate: string;
        endDate: string;
      };
      [CompareDateRangeTypes.PREVIOUSPERIOD]: {
        value: "previousPeriod";
      };
      [CompareDateRangeTypes.SAMEPERIODPRIORYEAR]: {
        value: "samePeriodPriorYear";
      };
    }[T];
